// TopNav — port of design/v3/Chrome.jsx.
// Phase 6.C-5 — viewport-aware: 모바일에서는 nav 숨김 (BottomTabBar 가 활성).
// 2026-05-03 Track A — 우측 user 버튼 → 프로필/설정/로그아웃 dropdown menu.

import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon, Logo } from '../primitives';

const TABS = [
  { id: 'ops', label: '운영', icon: 'store', to: '/ops' },
  { id: 'inventory', label: '재고', icon: 'package', to: '/inventory' },
  { id: 'schedule', label: '작업일정', icon: 'calendar', to: '/schedule' },
  { id: 'settings', label: '설정', icon: 'settings', to: '/settings' },
];

export interface TopNavProps {
  userInitial?: string;
  onLogout?: () => void;
  viewport?: 'mobile' | 'tablet' | 'desktop';
}

export function TopNav({ userInitial = '나', onLogout, viewport = 'desktop' }: TopNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeId =
    TABS.find((t) => location.pathname.startsWith(t.to))?.id ?? 'ops';
  const isMobile = viewport === 'mobile';
  const isTablet = viewport === 'tablet';

  // Dropdown state — 외부 클릭 + Escape 시 close.
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const closeAnd = (fn?: () => void) => {
    setOpen(false);
    if (fn) fn();
  };

  return (
    <header
      style={{
        height: 'var(--nav-h)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--nav-pad-x)',
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-weak)',
        gap: 'var(--nav-gap)',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 20,
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      <Logo size={isMobile ? 20 : 22} />
      {/* 모바일에서는 nav 숨김 (BottomTabBar 가 활성). 태블릿은 컴팩트. */}
      {!isMobile && (
        <nav
          style={{
            display: 'flex',
            gap: 4,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {TABS.map((t) => {
            const active = activeId === t.id;
            return (
              <NavLink
                key={t.id}
                to={t.to}
                style={{
                  padding: isTablet ? '7px 10px' : '8px 14px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--primary-strong)' : 'var(--fg-2)',
                  background: active ? 'var(--primary-bg)' : 'transparent',
                  border: active
                    ? '1px solid var(--primary-border)'
                    : '1px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  textDecoration: 'none',
                  flexShrink: 0,
                }}
              >
                <Icon
                  name={t.icon}
                  size={16}
                  color={active ? 'var(--primary-strong)' : 'var(--fg-3)'}
                />
                {!isTablet && t.label}
              </NavLink>
            );
          })}
        </nav>
      )}
      {isMobile && <div style={{ flex: 1 }} />}

      {/* 사용자 메뉴 — initial 버튼 + dropdown (프로필 / 설정 / 로그아웃) */}
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="사용자 메뉴"
          title="사용자 메뉴"
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: 'var(--earth)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
          }}
        >
          {userInitial}
        </button>
        {open && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              minWidth: 160,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-weak)',
              borderRadius: 10,
              boxShadow: '0 8px 20px rgba(0,0,0,0.10)',
              padding: 6,
              zIndex: 30,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: 'calc(100vw - 16px)',
            }}
          >
            <MenuItem
              icon="user"
              label="프로필"
              onClick={() => closeAnd(() => navigate('/profile'))}
            />
            <MenuItem
              icon="settings"
              label="설정"
              onClick={() => closeAnd(() => navigate('/settings'))}
            />
            <div
              style={{
                height: 1,
                background: 'var(--border-weak)',
                margin: '4px 0',
              }}
            />
            <MenuItem
              icon="logout"
              label="로그아웃"
              tone="danger"
              onClick={() => closeAnd(onLogout)}
            />
          </div>
        )}
      </div>
    </header>
  );
}

interface MenuItemProps {
  icon: string;
  label: string;
  onClick?: () => void;
  tone?: 'default' | 'danger';
}

function MenuItem({ icon, label, onClick, tone = 'default' }: MenuItemProps) {
  const [hover, setHover] = useState(false);
  const color = tone === 'danger' ? 'var(--danger)' : 'var(--fg-1)';
  const iconColor = tone === 'danger' ? 'var(--danger)' : 'var(--fg-3)';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 6,
        background: hover ? 'var(--bg-subtle)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 13,
        fontWeight: 500,
        color,
        width: '100%',
      }}
    >
      <Icon name={icon} size={14} color={iconColor} />
      {label}
    </button>
  );
}
